/**
 * Keep the cached permission map fresh without ending the user's session.
 *
 * THE PROBLEM. bswebapp fetches /userinfo exactly once — Initialize(), at app
 * boot — and caches `role_settings` in Vuex. The server, by contrast, reads
 * user_roles.role_settings live on every request. So the moment an admin edits
 * a role, the two disagree: the server refuses what it now refuses, while the
 * open tab keeps drawing its menu and its v-can gates from the map it loaded at
 * login. The user sees buttons that error, and nothing tells them to reload.
 *
 * Forcing a logout would fix the staleness by throwing away whatever they were
 * typing. This does it quietly instead: bsapi stamps every API response with
 *
 *     X-Perms-Version: <usertype>.<unix ts of the role's last edit>
 *
 * (see App\Http\Middleware\StampPermissionsVersion). We remember the value, and
 * when a later response carries a different one we re-fetch /userinfo and
 * commit it. The menu and every v-can gate re-evaluate on the next render.
 *
 * NOT A SECURITY CONTROL. Revocation already takes effect server-side on the
 * very next request — this only stops the UI lying about it.
 *
 * DESIGN NOTES
 *
 *  - The FIRST version seen is recorded, never acted on. At boot Initialize()
 *    has just fetched /userinfo, so the map is current by definition; refreshing
 *    again would be a wasted round-trip on every cold start.
 *  - Single-flight. A page load fires many parallel requests and they all carry
 *    the new version, so without a latch each one would queue its own
 *    /userinfo. `inFlight` collapses them into one.
 *  - The /userinfo call is made with plain fetch, not fetchWrapper, so that a
 *    failure here can never recurse back into this module or trip the
 *    wrapper's logout-on-401 path. A failed refresh is a no-op: the client
 *    keeps the map it has and tries again on the next differing response.
 */

import { store } from '@/storeVuex';

export const PERMS_VERSION_HEADER = 'x-perms-version';

let lastSeen: string | null = null;
let inFlight: Promise<void> | null = null;

/** Test seam: forget what we have seen. */
export function resetPermissionsVersion(): void {
    lastSeen = null;
    inFlight = null;
}

/** Test seam: what the module currently believes is current. */
export function currentPermissionsVersion(): string | null {
    return lastSeen;
}

/**
 * Feed a response's X-Perms-Version in. Returns true when this call triggered a
 * refresh, which is what the specs assert on.
 */
export function observePermissionsVersion(version: string | null | undefined, refresh: () => Promise<void> = refreshUserInfo): boolean {
    const value = typeof version === 'string' ? version.trim() : '';
    if (!value) return false;

    // First sighting: adopt it silently. The map we hold was fetched at boot.
    if (lastSeen === null) {
        lastSeen = value;
        return false;
    }

    if (value === lastSeen) return false;

    // Adopt the new version BEFORE the refresh resolves. Otherwise every other
    // in-flight response carrying the same new value would each see a mismatch
    // and queue another refresh.
    lastSeen = value;

    if (!inFlight) {
        inFlight = refresh()
            .catch(() => {
                // Swallowed on purpose — see the header. Forget the version so
                // the next response retries rather than assuming we are current.
                lastSeen = null;
            })
            .finally(() => {
                inFlight = null;
            });
    }

    return true;
}

/** Pull a fresh user object (including role_settings) and commit it. */
async function refreshUserInfo(): Promise<void> {
    const session: any = (store as any)?.state?.user;
    const token = session?.token || session?.user?.token;
    if (!token) return;

    const baseUrl = import.meta.env.VITE_API_URL_API;
    const body: Record<string, string> = {};
    if (session?.csrfToken) body._csrf = session.csrfToken;

    const response = await fetch(`${baseUrl}/userinfo`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) throw new Error(`userinfo ${response.status}`);

    const data = await response.json();
    // Same mutation Initialize() commits, so there is one shape of truth.
    (store as any).commit('refreshUserData', data);
}

/**
 * Convenience for a `fetch` Response or an axios response — both expose the
 * header differently, and neither is worth a branch at each call site.
 */
export function observeFromResponse(response: any): void {
    try {
        const headers = response?.headers;
        if (!headers) return;

        const value =
            typeof headers.get === 'function'
                ? headers.get(PERMS_VERSION_HEADER)
                : headers[PERMS_VERSION_HEADER] ?? headers[PERMS_VERSION_HEADER.toUpperCase()];

        observePermissionsVersion(value);
    } catch {
        // Never let header bookkeeping break a response the caller needs.
    }
}
